"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useDailyEvent } from "@daily-co/daily-react";
import type {
  DailyEventObjectNonFatalError,
  DailyEventObjectFatalError,
  DailyEventObjectCameraError,
  DailyEventObjectTrack,
} from "@daily-co/daily-js";
import { getDailyErrorType } from "~/components/chat/components/dailyjs/dailyjs-monitoring";

export type MediaErrorType =
  | "permissions"
  | "device-in-use"
  | "not-found"
  | "device-lost"
  | "connection-interrupted"
  | "session-ended"
  | "join-failed"
  | "unknown";
export type AffectedMedia = "camera" | "mic";
type BlockedMedia = "video" | "audio";
type PermissionBlockSource = "user" | "browser";
type MediaAccessTarget = "camera" | "mic" | "camera-and-mic";

export interface MediaError {
  type: MediaErrorType;
  message: string;
  affectedMedia: AffectedMedia[];
  isRecovering: boolean;
  isSevere: boolean; // Upgraded after timeout
  helpText?: string;
}

export function buildConnectionInterruptedError(): MediaError {
  return {
    type: "connection-interrupted",
    message:
      "Veza je prekinuta, ali razgovor te čeka. Pridruži se ponovo da nastaviš tamo gde ste stali.",
    affectedMedia: [],
    isRecovering: false,
    isSevere: false,
    helpText:
      "Ako ponovno pridruživanje ne uspe, osveži stranicu i pokušaj još jednom.",
  };
}

export function buildUnexpectedLeaveError(joinedAt: number | null): MediaError {
  const shortJoin = joinedAt !== null && Date.now() - joinedAt < 15_000;

  return {
    type: "session-ended",
    message: shortJoin
      ? "Veza sa pozivom je prekinuta odmah po povezivanju."
      : "Veza sa pozivom je prekinuta tokom razgovora.",
    affectedMedia: [],
    isRecovering: false,
    isSevere: true,
    helpText:
      "Osveži stranicu i pokušaj ponovo. Ako se problem ponovi, kontaktiraj podršku i navedi vreme sesije.",
  };
}

interface UseDailyMediaErrorsOptions {
  /** Callback to simulate errors in test mode */
  onSimulateError?: (type: MediaErrorType) => void;
  /** Callback for Daily fatal errors so callers can add diagnostics */
  onFatalError?: (event: DailyEventObjectFatalError) => void;
}

interface UseDailyMediaErrorsReturn {
  error: MediaError | null;
  clearError: () => void;
  /** For test mode: manually trigger error states */
  simulateError: (type: MediaErrorType) => void;
  simulateRecovery: () => void;
}

const SEVERITY_UPGRADE_TIMEOUT_MS = 15000; // 15 seconds

/**
 * Detects browser for appropriate error messages
 */
function detectBrowser(): "safari" | "chrome" | "firefox" | "edge" | "other" {
  if (typeof navigator === "undefined") return "other";

  const ua = navigator.userAgent;
  if (ua.includes("Safari") && !ua.includes("Chrome")) return "safari";
  if (ua.includes("Edg")) return "edge";
  if (ua.includes("Chrome")) return "chrome";
  if (ua.includes("Firefox")) return "firefox";
  return "other";
}

function detectPlatform(): "android" | "ios" | "macos" | "windows" | "other" {
  if (typeof navigator === "undefined") return "other";

  const ua = navigator.userAgent;
  if (ua.includes("Android")) return "android";
  if (ua.includes("iPhone") || ua.includes("iPad")) return "ios";
  if (ua.includes("Mac")) return "macos";
  if (ua.includes("Windows")) return "windows";
  return "other";
}

/**
 * Get browser-specific permission instructions in Serbian
 */
export function getPermissionInstructions(): {
  browser: string;
  instructions: string;
} {
  const browser = detectBrowser();

  switch (browser) {
    case "safari":
      return {
        browser: "Safari",
        instructions:
          "U Safari-ju kliknite na meni 'Safari' → 'Podešavanja za ovaj sajt…' → postavite Kamera i Mikrofon na 'Dozvoli', pa osvežite stranicu.",
      };
    case "chrome":
      return {
        browser: "Chrome",
        instructions:
          "Kliknite na ikonu katanca u adresnoj traci → 'Podešavanja sajta' → omogućite Kameru i Mikrofon, pa osvežite stranicu.",
      };
    case "firefox":
      return {
        browser: "Firefox",
        instructions:
          "Kliknite na ikonu štita/katanca u adresnoj traci → uklonite blokadu za Kameru i Mikrofon, pa osvežite stranicu.",
      };
    case "edge":
      return {
        browser: "Edge",
        instructions:
          "Kliknite na ikonu katanca u adresnoj traci → 'Dozvole za sajt' → omogućite Kameru i Mikrofon, pa osvežite stranicu.",
      };
    default:
      return {
        browser: "pretraživač",
        instructions:
          "Omogućite pristup kameri i mikrofonu u podešavanjima pretraživača za ovaj sajt, pa osvežite stranicu.",
      };
  }
}

export function getSystemPermissionInstructions(): string {
  switch (detectPlatform()) {
    case "android":
      return "Na Android telefonu otvorite Podešavanja uređaja → Aplikacije → vaš pregledač → Dozvole, pa uključite Kameru i Mikrofon.";
    case "ios":
      return "Na iPhone/iPad uređaju otvorite Settings → Safari ili Chrome → Camera i Microphone, pa dozvolite pristup.";
    case "macos":
      return "Na Mac-u otvorite System Settings → Privacy & Security → Camera i Microphone, pa dozvolite pristup za pregledač koji koristite.";
    case "windows":
      return "Na Windows-u otvorite Settings → Privacy & security → Camera i Microphone, pa dozvolite pristup za pregledač koji koristite.";
    default:
      return "Proverite sistemska podešavanja uređaja i dozvolite pristup kameri i mikrofonu za pregledač koji koristite.";
  }
}

function getBlockedMediaLabel(blockedMedia?: readonly BlockedMedia[]): string {
  const media = blockedMedia ?? ["video", "audio"];
  const hasVideo = media.includes("video");
  const hasAudio = media.includes("audio");

  if (hasVideo && hasAudio) return "kameri i mikrofonu";
  if (hasVideo) return "kameri";
  return "mikrofonu";
}

function getAffectedMedia(
  blockedMedia?: readonly BlockedMedia[],
): AffectedMedia[] {
  const media = blockedMedia ?? ["video", "audio"];
  const affected: AffectedMedia[] = [];

  if (media.includes("video")) {
    affected.push("camera");
  }
  if (media.includes("audio")) {
    affected.push("mic");
  }

  return affected.length > 0 ? affected : ["camera", "mic"];
}

function getBlockedMediaForTarget(target: MediaAccessTarget): BlockedMedia[] {
  switch (target) {
    case "camera":
      return ["video"];
    case "mic":
      return ["audio"];
    default:
      return ["video", "audio"];
  }
}

function getUnavailableMediaLabel(target: MediaAccessTarget): string {
  switch (target) {
    case "camera":
      return "kameri";
    case "mic":
      return "mikrofonu";
    default:
      return "kameri i mikrofonu";
  }
}

export function getPermissionErrorContent(options?: {
  blockedBy?: PermissionBlockSource;
  blockedMedia?: readonly BlockedMedia[];
}): {
  affectedMedia: AffectedMedia[];
  message: string;
  helpText: string;
} {
  const blockedMedia = options?.blockedMedia;
  const blockedBy = options?.blockedBy;
  const blockedMediaLabel = getBlockedMediaLabel(blockedMedia);
  const { instructions: browserInstructions } = getPermissionInstructions();
  const systemInstructions = getSystemPermissionInstructions();

  if (blockedBy === "user") {
    return {
      affectedMedia: getAffectedMedia(blockedMedia),
      message: `Pristup ${blockedMediaLabel} je blokiran u pregledaču za ovaj sajt.`,
      helpText: `${browserInstructions} Ako ste dozvolu već uključili, zatvorite tab sa pozivom i otvorite ga ponovo.`,
    };
  }

  if (blockedBy === "browser") {
    return {
      affectedMedia: getAffectedMedia(blockedMedia),
      message: `Pregledač ili podešavanja uređaja trenutno blokiraju pristup ${blockedMediaLabel}.`,
      helpText: `${systemInstructions} Ako su dozvole već uključene, potpuno zatvorite pregledač i druge aplikacije koje koriste kameru ili mikrofon, pa pokušajte ponovo.`,
    };
  }

  return {
    affectedMedia: getAffectedMedia(blockedMedia),
    message: `Ne možemo da pristupimo ${blockedMediaLabel}.`,
    helpText: `${browserInstructions} Takođe proverite dozvole za pregledač u podešavanjima uređaja. Ako je sve dozvoljeno, zatvorite druge aplikacije ili tabove koji možda koriste kameru ili mikrofon, pa pokušajte ponovo.`,
  };
}

export function classifyMediaAccessError(
  err: unknown,
  target: MediaAccessTarget = "camera-and-mic",
): {
  type: "permissions" | "not-found" | "device-in-use" | "unknown";
  message: string;
  helpText?: string;
} {
  const errorName = err instanceof Error ? err.name : "";
  const errorMessage = err instanceof Error ? err.message : String(err);
  const normalizedError = `${errorName} ${errorMessage}`.toLowerCase();

  if (
    errorName === "NotAllowedError" ||
    errorName === "PermissionDeniedError"
  ) {
    const permissionError = getPermissionErrorContent({
      blockedMedia: getBlockedMediaForTarget(target),
    });

    return {
      type: "permissions",
      message: permissionError.message,
      helpText: permissionError.helpText,
    };
  }

  if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
    return {
      type: "not-found",
      message:
        target === "camera"
          ? "Kamera nije pronađena."
          : target === "mic"
            ? "Mikrofon nije pronađen."
            : "Kamera ili mikrofon nisu pronađeni.",
    };
  }

  if (
    errorName === "NotReadableError" ||
    errorName === "AbortError" ||
    errorName === "TrackStartError" ||
    normalizedError.includes("notreadableerror") ||
    normalizedError.includes("could not start video source") ||
    normalizedError.includes("device in use")
  ) {
    return {
      type: "device-in-use",
      message: `Ne možemo da pristupimo ${getUnavailableMediaLabel(target)} jer je uređaj zauzet ili trenutno nedostupan.`,
      helpText:
        "Zatvorite druge aplikacije i tabove koji koriste kameru ili mikrofon. Ako problem ostane, potpuno zatvorite pregledač ili restartujte uređaj pa pokušajte ponovo.",
    };
  }

  return {
    type: "unknown",
    message: `Došlo je do greške pri pristupu ${getUnavailableMediaLabel(target)}.`,
    helpText:
      "Osvežite stranicu. Ako problem ostane, pokušajte ponovo u drugom pregledaču ili restartujte uređaj.",
  };
}

/**
 * Hook for centralized Daily media error handling.
 * Detects permission errors, device disconnects, and provides recovery state.
 */
export function useDailyMediaErrors(
  options: UseDailyMediaErrorsOptions = {},
): UseDailyMediaErrorsReturn {
  const [error, setError] = useState<MediaError | null>(null);
  const severityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Listen for nonfatal errors (device lost, track ended)
  useDailyEvent(
    "nonfatal-error",
    useCallback((event: DailyEventObjectNonFatalError) => {
      // Check for track ended / device lost scenarios
      const errorMsg = event.errorMsg ?? "";
      if (
        event.type === "input-settings-error" ||
        errorMsg.includes("track ended") ||
        errorMsg.includes("Local audio track ended")
      ) {
        // Determine which media is affected
        const affectedMedia: AffectedMedia[] = [];

        if (
          errorMsg.toLowerCase().includes("audio") ||
          errorMsg.toLowerCase().includes("mic")
        ) {
          affectedMedia.push("mic");
        }
        if (
          errorMsg.toLowerCase().includes("video") ||
          errorMsg.toLowerCase().includes("cam")
        ) {
          affectedMedia.push("camera");
        }
        // Default to mic if we can't determine
        if (affectedMedia.length === 0) {
          affectedMedia.push("mic");
        }

        setError({
          type: "device-lost",
          message:
            "Izgubili smo vezu sa vašim mikrofonom. Proverite priključak slušalica ili izaberite drugi mikrofon.",
          affectedMedia,
          isRecovering: true,
          isSevere: false,
        });

        // Start timer to upgrade severity if not recovered
        if (severityTimeoutRef.current) {
          clearTimeout(severityTimeoutRef.current);
        }
        severityTimeoutRef.current = setTimeout(() => {
          setError((prev) => {
            if (prev && prev.type === "device-lost") {
              return {
                ...prev,
                message:
                  "I dalje ne možemo pristupiti vašem mikrofonu. Pokušajte da isključite i ponovo priključite slušalice, izaberete drugi mikrofon, ili osvežite stranicu.",
                isSevere: true,
              };
            }
            return prev;
          });
        }, SEVERITY_UPGRADE_TIMEOUT_MS);
      }
    }, []),
  );

  // Listen for fatal errors
  useDailyEvent(
    "error",
    useCallback(
      (event: DailyEventObjectFatalError) => {
        options.onFatalError?.(event);
        const errorType = getDailyErrorType(event);

        switch (errorType) {
          case "ejected":
            setError({
              type: "session-ended",
              message: "Vaša sesija je završena.",
              affectedMedia: [],
              isRecovering: false,
              isSevere: true,
            });
            break;

          case "exp-room":
          case "exp-token":
            setError({
              type: "session-ended",
              message: "Vreme za ovu sesiju je isteklo.",
              affectedMedia: [],
              isRecovering: false,
              isSevere: true,
            });
            break;

          case "nbf-room":
          case "nbf-token":
            setError({
              type: "session-ended",
              message:
                "Ova sesija još uvek nije dostupna. Pokušajte ponovo kasnije.",
              affectedMedia: [],
              isRecovering: false,
              isSevere: true,
            });
            break;

          case "no-room":
            setError({
              type: "join-failed",
              message:
                "Došlo je do privremene greške pri povezivanju sa pozivom.",
              affectedMedia: [],
              isRecovering: false,
              isSevere: false,
              helpText:
                "Pokušajte ponovo. Ako se problem ponovi, osvežite stranicu i pokušajte još jednom.",
            });
            break;

          case "meeting-full":
            setError({
              type: "join-failed",
              message:
                "Poziv je trenutno popunjen. Sačekajte trenutak i pokušajte ponovo.",
              affectedMedia: [],
              isRecovering: false,
              isSevere: false,
              helpText:
                "Ovo se može desiti ako se neko upravo ponovo povezuje ili je prethodni pokušaj ostao aktivan nekoliko sekundi.",
            });
            break;

          case "connection-error":
            setError(buildConnectionInterruptedError());
            break;

          case "not-allowed":
            {
              const permissionError = getPermissionErrorContent();
              setError({
                type: "permissions",
                message: permissionError.message,
                affectedMedia: permissionError.affectedMedia,
                isRecovering: false,
                isSevere: true,
                helpText: permissionError.helpText,
              });
            }
            break;

          default: {
            const errorMsg = event.errorMsg ?? "";
            if (
              errorMsg.includes("NotAllowedError") ||
              errorMsg.includes("Permission denied")
            ) {
              const permissionError = getPermissionErrorContent();
              setError({
                type: "permissions",
                message: permissionError.message,
                affectedMedia: permissionError.affectedMedia,
                isRecovering: false,
                isSevere: true,
                helpText: permissionError.helpText,
              });
            }
            break;
          }
        }
      },
      [options],
    ),
  );

  // Listen for camera-error event directly
  useDailyEvent(
    "camera-error",
    useCallback((event: DailyEventObjectCameraError) => {
      const errorType = event.error?.type;
      switch (errorType) {
        case "permissions": {
          const blockedBy =
            "blockedBy" in event.error
              ? (event.error.blockedBy as PermissionBlockSource | undefined)
              : undefined;
          const blockedMedia =
            "blockedMedia" in event.error
              ? (event.error.blockedMedia as BlockedMedia[] | undefined)
              : undefined;
          const permissionError = getPermissionErrorContent({
            blockedBy,
            blockedMedia,
          });
          setError({
            type: "permissions",
            message: permissionError.message,
            affectedMedia: permissionError.affectedMedia,
            isRecovering: false,
            isSevere: true,
            helpText: permissionError.helpText,
          });
          break;
        }

        case "cam-in-use":
          setError({
            type: "device-in-use",
            message:
              "Kamera je zauzeta u drugom programu ili kartici pretraživača. Zatvorite je tamo pa osvežite ovu stranicu.",
            affectedMedia: ["camera"],
            isRecovering: false,
            isSevere: true,
          });
          break;

        case "mic-in-use":
          setError({
            type: "device-in-use",
            message:
              "Mikrofon je zauzet u drugom programu ili kartici pretraživača. Zatvorite ga tamo pa osvežite ovu stranicu.",
            affectedMedia: ["mic"],
            isRecovering: false,
            isSevere: true,
          });
          break;

        case "cam-mic-in-use":
          setError({
            type: "device-in-use",
            message:
              "Kamera i mikrofon su zauzeti u drugom programu ili kartici pretraživača. Zatvorite ih tamo pa osvežite ovu stranicu.",
            affectedMedia: ["camera", "mic"],
            isRecovering: false,
            isSevere: true,
          });
          break;

        case "not-found": {
          const missingMedia =
            "missingMedia" in event.error
              ? (event.error.missingMedia as BlockedMedia[] | undefined)
              : undefined;
          setError({
            type: "not-found",
            message:
              "Kamera ili mikrofon nisu pronađeni. Proverite da li su priključeni i pokušajte ponovo.",
            affectedMedia: getAffectedMedia(missingMedia),
            isRecovering: false,
            isSevere: true,
          });
          break;
        }

        case "unknown":
          setError({
            type: "unknown",
            message:
              "Došlo je do greške sa kamerom ili mikrofonom. Pokušajte da osvežite stranicu.",
            affectedMedia: ["camera", "mic"],
            isRecovering: false,
            isSevere: true,
            helpText:
              "Proverite dozvole u pregledaču i na uređaju, zatvorite druge aplikacije ili tabove koji možda koriste kameru ili mikrofon, pa pokušajte ponovo. Ako problem ostane, pokušajte u drugom pregledaču ili restartujte uređaj.",
          });
          break;

        default:
          setError({
            type: "unknown",
            message:
              "Došlo je do greške sa kamerom ili mikrofonom. Pokušajte da osvežite stranicu.",
            affectedMedia: ["camera", "mic"],
            isRecovering: false,
            isSevere: true,
            helpText:
              "Ako problem ostane, pokušajte ponovo u drugom pregledaču ili restartujte uređaj.",
          });
          break;
      }
    }, []),
  );

  // Listen for track-started to auto-clear device-lost errors
  useDailyEvent(
    "track-started",
    useCallback((event: DailyEventObjectTrack) => {
      if (event.participant?.local && event.track?.kind === "audio") {
        // Local audio track recovered - clear device-lost error
        setError((prev) => {
          if (prev?.type === "device-lost") {
            if (severityTimeoutRef.current) {
              clearTimeout(severityTimeoutRef.current);
              severityTimeoutRef.current = null;
            }
            return null;
          }
          return prev;
        });
      }
    }, []),
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (severityTimeoutRef.current) {
        clearTimeout(severityTimeoutRef.current);
      }
    };
  }, []);

  const clearError = useCallback(() => {
    if (severityTimeoutRef.current) {
      clearTimeout(severityTimeoutRef.current);
      severityTimeoutRef.current = null;
    }
    setError(null);
  }, []);

  // Test mode simulation functions
  const simulateError = useCallback(
    (type: MediaErrorType) => {
      switch (type) {
        case "permissions":
          {
            const permissionError = getPermissionErrorContent();
            setError({
              type: "permissions",
              message: permissionError.message,
              affectedMedia: permissionError.affectedMedia,
              isRecovering: false,
              isSevere: true,
              helpText: permissionError.helpText,
            });
          }
          break;

        case "device-in-use":
          setError({
            type: "device-in-use",
            message:
              "Kamera je zauzeta u drugom programu ili kartici pretraživača. Zatvorite je tamo pa osvežite ovu stranicu.",
            affectedMedia: ["camera"],
            isRecovering: false,
            isSevere: true,
          });
          break;

        case "not-found":
          setError({
            type: "not-found",
            message:
              "Kamera ili mikrofon nisu pronađeni. Proverite da li su priključeni i pokušajte ponovo.",
            affectedMedia: ["camera", "mic"],
            isRecovering: false,
            isSevere: true,
          });
          break;

        case "device-lost":
          setError({
            type: "device-lost",
            message:
              "Izgubili smo vezu sa vašim mikrofonom. Proverite priključak slušalica ili izaberite drugi mikrofon.",
            affectedMedia: ["mic"],
            isRecovering: true,
            isSevere: false,
          });

          if (severityTimeoutRef.current) {
            clearTimeout(severityTimeoutRef.current);
          }
          severityTimeoutRef.current = setTimeout(() => {
            setError((prev) => {
              if (prev && prev.type === "device-lost") {
                return {
                  ...prev,
                  message:
                    "I dalje ne možemo pristupiti vašem mikrofonu. Pokušajte da isključite i ponovo priključite slušalice, izaberete drugi mikrofon, ili osvežite stranicu.",
                  isSevere: true,
                };
              }
              return prev;
            });
          }, SEVERITY_UPGRADE_TIMEOUT_MS);
          break;

        default:
          setError({
            type: "unknown",
            message: "Došlo je do nepoznate greške sa medijskim uređajima.",
            affectedMedia: ["camera", "mic"],
            isRecovering: false,
            isSevere: true,
          });
          break;
      }

      options.onSimulateError?.(type);
    },
    [options],
  );

  const simulateRecovery = useCallback(() => {
    clearError();
  }, [clearError]);

  return {
    error,
    clearError,
    simulateError,
    simulateRecovery,
  };
}
